/**
 * HubDocumentPage — read-only view for one Hub document.
 *
 * Editing lives in the Security space's Document Register (DocumentDetail.tsx), which edits
 * the exact same underlying document via the same documentApi - a more capable editor
 * (full/per-section rich text, status, review date, register reassignment) than anything
 * worth duplicating here. Hub is purely a reading surface: np-detail-layout with a sticky
 * meta-rail (ownership/classification/version/status byline, same chrome ISMSScopePage and
 * AiUseCaseDetail use) stacked with an auto-generated, scroll-spied table of contents, and
 * the body rendered through RichTextView - the same reader every other long-text field in
 * the app uses - rather than a bare div.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { RichTextView } from '../../components/RichTextView';
import { documentApi, documentsApi, raciApi, peopleApi, departmentsApi } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { HUB_CATEGORIES, STATUS_META, effectiveStatus, formatUpdated, stripMetadataPreamble, extractHeadings } from './hubUtils';

export const HubDocumentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const contentRef = useRef<HTMLDivElement>(null);
  const [activeHeading, setActiveHeading] = useState<string>('');

  const { data: doc, isLoading, error } = useQuery<any>({
    queryKey: ['hubDocument', id],
    queryFn: () => documentApi.getOne(id!),
    enabled: !!id,
    staleTime: 0,
  });

  const { data: registers = [] } = useQuery<any[]>({ queryKey: ['documentRegisters'], queryFn: documentApi.getRegisters });
  const register = registers.find((r) => r.registerId === doc?.entityId);
  // A DPIA's entityId is a useCaseId/activityId, never a registerId, so it can never match
  // above - resolved by documentType instead, the same branch every other DPIA-aware query in
  // Hub already takes.
  const category = doc?.documentType === 'DPIA'
    ? HUB_CATEGORIES.find((c) => c.dpiaOnly)
    : HUB_CATEGORIES.find((c) => c.registerName === register?.name);

  // Read-only RACI summary - same artefactType/data source as DocumentDetail's own (editable)
  // RaciAssignments, but Hub is a pure reading surface (see this file's own doc-comment), so
  // this renders the assignments directly rather than pulling in that component's add/edit/
  // delete UI wholesale.
  const { data: raciAssignments = [] } = useQuery<any[]>({
    queryKey: ['raci', 'document', doc?.documentId],
    queryFn: () => raciApi.getByArtefact('document', doc!.documentId),
    enabled: !!doc?.documentId,
  });
  const { data: people = [] } = useQuery<any[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const personName = (personId: string) => people.find((p) => p.personId === personId)?.personName || personId;
  const { data: departments = [] } = useQuery<any[]>({ queryKey: ['departments'], queryFn: departmentsApi.getAll });
  const departmentName = (departmentId: string) => departments.find((d) => d.departmentId === departmentId)?.name || departmentId;
  const RACI_ROLE_LABELS: Record<string, string> = { R: 'Responsible', A: 'Accountable', C: 'Consulted', I: 'Informed' };
  const RACI_ROLE_TONES: Record<string, string> = { R: 'primary', A: 'success', C: 'info', I: 'warning' };

  // Table of contents, built from the headings actually present in this document, then
  // scroll-spied the same way ISMSScopePage tracks which section is on screen.
  //
  // h2 is included alongside h3-h5 because DocumentDetail's section-based editor stores every
  // top-level section title as an <h2> (assembleSectionsToHtml) - a document authored that way
  // had its actual section titles missing from this nav entirely until this was fixed, since
  // only the sub-headings within each section were ever picked up.
  //
  // Anchor ids are slugified from the heading's own text, not its position - a positional id
  // (the previous `hub-sec-${id}-${i}` scheme) shifts every time a section is added, removed,
  // or reordered above it, silently breaking every link anyone had already shared to it.
  const [headings, setHeadings] = useState<{ id: string; text: string; level: number }[]>([]);
  useEffect(() => {
    if (!contentRef.current) { setHeadings([]); return; }
    const found = extractHeadings(contentRef.current).map((h) => {
      h.node.id = h.id;
      return { id: h.id, text: h.text, level: h.level };
    });
    setHeadings(found);
    if (found.length) setActiveHeading(found[0].id);
  }, [doc?.content, id]);

  const copyHeadingLink = (headingId: string) => {
    const url = `${window.location.origin}${window.location.pathname}#${headingId}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success('Link copied to clipboard'),
      () => toast.error('Could not copy link'),
    );
  };

  useEffect(() => {
    if (!headings.length) return;
    const observers: IntersectionObserver[] = [];
    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveHeading(h.id); },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [headings]);

  const scrollToHeading = (headingId: string) => {
    document.getElementById(headingId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${headingId}`);
  };

  // Jumping straight to a shared/bookmarked link (e.g. #access-review) only works once the
  // heading ids actually exist in the DOM, which happens after this render - a plain browser
  // anchor-on-load can't see them yet.
  useEffect(() => {
    if (!headings.length || !window.location.hash) return;
    const targetId = decodeURIComponent(window.location.hash.slice(1));
    document.getElementById(targetId)?.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [headings]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
      </div>
    );
  }
  if (error || !doc) {
    return <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Could not load this document.</div>;
  }

  const status = effectiveStatus(doc);
  const statusMeta = STATUS_META[status];

  return (
    <div>
      <nav className="np-crumb mb-2">
        <Link to="/hub">Hub</Link>
        <span className="np-crumb-sep">›</span>
        {category ? <Link to={`/hub/${category.key}`}>{category.shortLabel}</Link> : <span>—</span>}
        <span className="np-crumb-sep">›</span>
        <span className="np-crumb-current">{doc.title || 'Document'}</span>
      </nav>

      <div className="np-detail-layout">
        <div className="np-meta-rail">
          <div className="np-card">
            <div className="np-card-body">
              <div className="np-meta-row"><span className="np-meta-label"><i className="ri-user-3-line"></i>Owner</span><span className="np-meta-value">{doc.author ? personName(doc.author) : '—'}</span></div>
              <div className="np-meta-row"><span className="np-meta-label"><i className="ri-team-line"></i>Team</span><span className="np-meta-value">{doc.departmentId ? departmentName(doc.departmentId) : '—'}</span></div>
              <div className="np-meta-row"><span className="np-meta-label"><i className="ri-lock-2-line"></i>Classification</span><span className="np-meta-value">{doc.confidentialityLevel || 'Internal'}</span></div>
              <div className="np-meta-row"><span className="np-meta-label"><i className="ri-git-branch-line"></i>Version</span><span className="np-meta-value">v{doc.versionLabel || '0.0'}</span></div>
              <div className="np-meta-row"><span className="np-meta-label"><i className="ri-time-line"></i>Last updated</span><span className="np-meta-value">{formatUpdated(doc.updatedAt)}</span></div>
              <div className="np-meta-row"><span className="np-meta-label"><i className="ri-calendar-check-line"></i>Next review</span><span className="np-meta-value">{formatUpdated(doc.nextReviewDate)}</span></div>
              <div className="np-meta-row">
                <span className="np-meta-label"><i className={statusMeta.icon}></i>Status</span>
                <span className={`np-chip ${statusMeta.tone}`}>{statusMeta.label}</span>
              </div>
            </div>
            <div className="hub-meta-actions">
              {doc.fileName && (
                <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => documentsApi.download(doc.documentId, doc.fileName)}>
                  <i className="ri-download-2-line"></i>Download original PDF
                </button>
              )}
              <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => window.print()}>
                <i className="ri-printer-line"></i>Print / Save as PDF
              </button>
              <Link to={`/documents/${doc.documentId}`} className="np-btn np-btn-primary np-btn-sm">
                <i className="ri-edit-line"></i>Edit in Document Register
              </Link>
            </div>
          </div>

          {raciAssignments.length > 0 && (
            <div className="np-card">
              <div className="np-card-head"><i className="ri-team-line"></i><h6>Responsibilities</h6></div>
              <div className="np-card-body py-2">
                {raciAssignments.map((a) => (
                  <div key={a.raciId} className="np-meta-row">
                    <span className="np-meta-label">
                      <span className={`np-chip np-tone-${RACI_ROLE_TONES[a.role] || 'neutral'}`} title={RACI_ROLE_LABELS[a.role] || a.role}>{a.role}</span>
                      {personName(a.personId)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {headings.length >= 2 && (
            <div className="np-card">
              <div className="np-card-head"><i className="ri-list-check-2"></i><h6>On this page</h6></div>
              <div className="np-nav-list">
                {headings.map((h) => (
                  <div key={h.id} className="hub-toc-row">
                    <button
                      type="button"
                      onClick={() => scrollToHeading(h.id)}
                      className={`np-nav-item ${activeHeading === h.id ? 'active' : ''}`}
                      style={{ paddingLeft: `${1.25 + h.level * 0.75}rem` }}
                    >
                      <span className="np-nav-item-title">{h.text}</span>
                    </button>
                    <button
                      type="button"
                      className="hub-toc-copy"
                      title="Copy link to this section"
                      onClick={() => copyHeadingLink(h.id)}
                    >
                      <i className="ri-link"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="hub-print-area">
          <PageHeader icon="ri-file-text-line" title={doc.title || 'Untitled'} subtitle={category?.label || 'Hub document'} />
          <div className="np-card">
            <div className="np-card-body" ref={contentRef}>
              <RichTextView value={stripMetadataPreamble(doc.content)} empty="No content yet." />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 hub-no-print">
        <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => navigate(-1)}>
          <i className="ri-arrow-left-line"></i>Back
        </button>
      </div>
    </div>
  );
};

export default HubDocumentPage;
